import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getErrorMessage, lessonManagementService, lessonService, lessonThumbnailSrc } from '../services/api';
import type { LessonAssignment, LessonAssignmentUpdate, LessonSummary } from '../types/api';
import { useToast } from './Toast';
import { EmptyState, LoadingState, Spinner } from './ui';
import ReadinessBadge from './ReadinessBadge';
import { formatDateTime, fromDateTimeLocalValue, isPast, toDateTimeLocalValue } from '../utils/format';
import {
  X,
  Plus,
  GripVertical,
  Eye,
  EyeOff,
  Trash2,
  BookOpen,
  Info,
  ArrowUp,
  ArrowDown,
  CalendarClock,
} from 'lucide-react';

interface ClassLessonManagerProps {
  classId: number;
  // Called with the class's assignments after every load and change, so the dashboard stays in step
  // (e.g. the lessons offered in the submissions panel).
  onChange?: (assignments: LessonAssignment[]) => void;
}

const sortAssignments = (items: LessonAssignment[]) => [...items].sort((a, b) => a.order_index - b.order_index);

const LessonTypeBadge: React.FC<{ type: LessonSummary['lesson_type'] }> = ({ type }) => (
  <span className={`badge shrink-0 ${type === 'official' ? 'badge-blue' : 'badge-purple'}`}>
    {type}
  </span>
);

// The lessons of one class, inline in the teacher dashboard: add, remove (with undo), reorder, hide, and per
// lesson whether submissions are open and until when.
const ClassLessonManager: React.FC<ClassLessonManagerProps> = ({ classId, onChange }) => {
  const toast = useToast();
  const [assignments, setAssignments] = useState<LessonAssignment[]>([]);
  const [allLessons, setAllLessons] = useState<LessonSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  // Deadline inputs being edited, by lesson id; committed on blur.
  const [deadlineDrafts, setDeadlineDrafts] = useState<Record<number, string>>({});
  // The class the component currently shows: answers for a class the teacher already left are dropped.
  const classIdRef = useRef(classId);
  classIdRef.current = classId;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const applyAssignments = useCallback((items: LessonAssignment[]) => {
    const sorted = sortAssignments(items);
    setAssignments(sorted);
    onChangeRef.current?.(sorted);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setShowAddLesson(false);
    setDeadlineDrafts({});
    Promise.all([
      lessonManagementService.getClassLessons(classId),
      lessonService.list(),
    ])
      .then(([assignmentsData, lessonsData]) => {
        if (cancelled) return;
        applyAssignments(assignmentsData);
        setAllLessons(lessonsData);
      })
      .catch((error) => {
        console.error('Error loading data:', error);
        if (!cancelled) setLoadError(getErrorMessage(error, 'Failed to load lesson data'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [classId, applyAssignments]);

  // Runs a change and then reloads the assignments; a spinner in the header shows it is in flight.
  const mutate = async (action: () => Promise<unknown>, failureMessage: string) => {
    const forClassId = classId;
    setBusy(true);
    try {
      await action();
      const fresh = await lessonManagementService.getClassLessons(forClassId);
      if (classIdRef.current === forClassId) {
        applyAssignments(fresh);
      }
      return true;
    } catch (error) {
      console.error(failureMessage, error);
      toast.error(`${failureMessage}: ${getErrorMessage(error)}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAddLesson = async (lessonId: number) => {
    // No order_index: the backend appends the lesson at the end.
    const added = await mutate(
      () => lessonManagementService.addLessonToClass(classId, { lesson_id: lessonId, is_visible: true }),
      'Failed to add lesson'
    );
    if (added) {
      setShowAddLesson(false);
    }
  };

  const handleRemoveLesson = async (assignment: LessonAssignment) => {
    const lesson = getLessonById(assignment.lesson_id);
    const removed = await mutate(
      () => lessonManagementService.removeLessonFromClass(classId, assignment.lesson_id),
      'Failed to remove lesson'
    );
    if (removed) {
      // Undo instead of a confirmation: removing is quick to reverse, and the class keeps the submissions.
      toast.success(`Removed "${lesson?.title ?? 'lesson'}" from the class`, {
        action: {
          label: 'Undo',
          onClick: () => {
            mutate(() => lessonManagementService.addLessonToClass(assignment.class_id, {
              lesson_id: assignment.lesson_id,
              order_index: assignment.order_index,
              is_visible: assignment.is_visible,
              submissions_enabled: assignment.submissions_enabled,
              submission_deadline: assignment.submission_deadline,
            }), 'Failed to restore the lesson');
          },
        },
      });
    }
  };

  const updateAssignment = (assignment: LessonAssignment, update: LessonAssignmentUpdate, failureMessage: string) =>
    mutate(() => lessonManagementService.updateLessonAssignment(classId, assignment.lesson_id, update), failureMessage);

  const commitDeadline = (assignment: LessonAssignment) => {
    const draft = deadlineDrafts[assignment.lesson_id];
    if (draft === undefined) {
      return;
    }
    setDeadlineDrafts(({ [assignment.lesson_id]: _, ...rest }) => rest);
    const deadline = fromDateTimeLocalValue(draft);
    if (deadline === assignment.submission_deadline || draft === toDateTimeLocalValue(assignment.submission_deadline)) {
      return;
    }
    updateAssignment(assignment, { submission_deadline: deadline }, 'Failed to update the deadline');
  };

  const moveAssignment = async (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= assignments.length || fromIndex === toIndex) {
      return;
    }
    const newAssignments = [...assignments];
    const [movedItem] = newAssignments.splice(fromIndex, 1);
    newAssignments.splice(toIndex, 0, movedItem);
    // Show the new order right away; the reload after the request settles it.
    setAssignments(newAssignments.map((assignment, index) => ({ ...assignment, order_index: index })));
    await mutate(
      () => lessonManagementService.reorderLessons(classId, newAssignments.map((assignment, index) => ({
        lesson_id: assignment.lesson_id,
        new_order_index: index,
      }))),
      'Failed to reorder lessons'
    );
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const fromIndex = draggedIndex;
    setDraggedIndex(null);
    if (fromIndex !== null) {
      await moveAssignment(fromIndex, dropIndex);
    }
  };

  const getLessonById = (lessonId: number) => {
    return allLessons.find(l => l.id === lessonId);
  };

  const availableLessons = allLessons.filter(
    lesson => !assignments.some(a => a.lesson_id === lesson.id)
  );

  if (loading) {
    return <LoadingState label="Loading lessons..." className="py-12" />;
  }

  if (loadError) {
    return (
      <div className="alert alert-error" role="alert">
        {loadError}
      </div>
    );
  }

  return (
    <div className="transition-colors">
      <div className="mb-5 flex flex-wrap justify-between items-center gap-3">
        <div>
          <h3 className="section-title flex items-center gap-2">
            Assigned Lessons
            {busy && <Spinner className="w-4 h-4" />}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage course curriculum ({assignments.length} lessons)</p>
        </div>
        <button
          onClick={() => setShowAddLesson(!showAddLesson)}
          className={`btn ${showAddLesson ? 'btn-secondary' : 'btn-primary'}`}
          aria-expanded={showAddLesson}
        >
          {showAddLesson ? <X /> : <Plus />}
          {showAddLesson ? 'Cancel' : 'Add Lesson'}
        </button>
      </div>

      {showAddLesson && (
        <div className="mb-6 p-4 sm:p-5 rounded-xl border border-blue-200 dark:border-blue-900/70 bg-blue-50/60 dark:bg-blue-950/20">
          <h4 className="font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
            <Plus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            Select a lesson to add
          </h4>
          {availableLessons.length === 0 ? (
            allLessons.length === 0 ? (
              <EmptyState title="No lessons found" className="bg-white dark:bg-gray-900">Create some lessons in the Content Library first.</EmptyState>
            ) : (
              <EmptyState title="All lessons assigned" className="bg-white dark:bg-gray-900">Every available lesson is already in this class.</EmptyState>
            )
          ) : (
            <div className="grid gap-2 md:grid-cols-2">
              {availableLessons.map(lesson => (
                <button
                  key={lesson.id}
                  onClick={() => handleAddLesson(lesson.id)}
                  disabled={busy}
                  className="w-full text-left px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg hover:border-blue-400 dark:hover:border-blue-600 transition-colors group disabled:opacity-60"
                >
                  <div className="flex justify-between items-center gap-3">
                    <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">{lesson.title}</span>
                      <LessonTypeBadge type={lesson.lesson_type} />
                      <ReadinessBadge readiness={lesson.readiness} plain />
                    </div>
                    <Plus className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {assignments.length === 0 ? (
        <EmptyState icon={<BookOpen />} title="No lessons assigned yet.">
          Click "Add Lesson" to get started.
        </EmptyState>
      ) : (
        <div className="grid gap-3">
          <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Info className="w-4 h-4 shrink-0" />
            <span>Drag or use the arrows to reorder, toggle visibility and set deadlines.</span>
          </p>
          {assignments.map((assignment, index) => {
            const lesson = getLessonById(assignment.lesson_id);
            if (!lesson) return null;
            const thumbnail = lessonThumbnailSrc(lesson);
            const deadlineValue = deadlineDrafts[assignment.lesson_id] ?? toDateTimeLocalValue(assignment.submission_deadline);
            const deadlinePassed = isPast(assignment.submission_deadline);

            return (
              <div
                key={assignment.lesson_id}
                draggable
                onDragStart={() => setDraggedIndex(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={() => setDraggedIndex(null)}
                className={`p-3 sm:p-4 border rounded-xl transition-all ${draggedIndex === index
                  ? 'opacity-50 border-blue-500 ring-2 ring-blue-500/20 bg-white dark:bg-gray-900'
                  : assignment.is_visible
                    ? 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                    : 'border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40'
                  } group`}
              >
                <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                  <div className="hidden sm:block text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors cursor-move" aria-hidden="true">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <span className="hidden sm:flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-xs font-semibold text-gray-600 dark:text-gray-300" title={`Position ${index + 1}`}>
                    {index + 1}
                  </span>
                  {thumbnail && (
                    <div className="hidden md:block w-20 h-14 flex-shrink-0 relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800">
                      <img
                        src={thumbnail}
                        alt=""
                        loading="lazy"
                        className={`w-full h-full object-cover transition-all ${!assignment.is_visible ? 'grayscale opacity-50' : ''}`}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`font-semibold tracking-tight ${assignment.is_visible ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                        <span className="sm:hidden text-gray-400 font-medium">{index + 1}. </span>{lesson.title}
                      </span>
                      <LessonTypeBadge type={lesson.lesson_type} />
                      <ReadinessBadge readiness={lesson.readiness} />
                      {!assignment.is_visible && (
                        <span className="badge badge-amber normal-case tracking-normal">
                          <EyeOff /> Hidden from students
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1 flex-shrink-0">
                    <div className="flex">
                      <button
                        onClick={() => moveAssignment(index, index - 1)}
                        disabled={busy || index === 0}
                        className="btn btn-ghost btn-icon disabled:opacity-30"
                        title="Move up"
                        aria-label="Move up"
                      >
                        <ArrowUp />
                      </button>
                      <button
                        onClick={() => moveAssignment(index, index + 1)}
                        disabled={busy || index === assignments.length - 1}
                        className="btn btn-ghost btn-icon disabled:opacity-30"
                        title="Move down"
                        aria-label="Move down"
                      >
                        <ArrowDown />
                      </button>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => updateAssignment(assignment, { is_visible: !assignment.is_visible }, 'Failed to update visibility')}
                        disabled={busy}
                        className={`btn btn-icon ${assignment.is_visible
                          ? 'btn-ghost text-emerald-600 dark:text-emerald-400'
                          : 'btn-ghost text-gray-400 dark:text-gray-500'
                          }`}
                        title={assignment.is_visible ? 'Hide from students' : 'Show to students'}
                        aria-label={assignment.is_visible ? 'Hide from students' : 'Show to students'}
                      >
                        {assignment.is_visible ? <Eye /> : <EyeOff />}
                      </button>
                      <button
                        onClick={() => handleRemoveLesson(assignment)}
                        disabled={busy}
                        className="btn btn-ghost-danger btn-icon"
                        title="Remove from class"
                        aria-label="Remove from class"
                      >
                        <Trash2 />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-6 text-sm sm:pl-[4.25rem]">
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={assignment.submissions_enabled}
                      disabled={busy}
                      onChange={(e) => updateAssignment(assignment, { submissions_enabled: e.target.checked }, 'Failed to update submissions')}
                      className="checkbox"
                    />
                    Accept submissions
                  </label>
                  <div className="flex flex-wrap items-center gap-2 text-gray-700 dark:text-gray-300">
                    <CalendarClock className="w-4 h-4 text-gray-400 shrink-0" />
                    <label htmlFor={`deadline-${classId}-${assignment.lesson_id}`}>Deadline</label>
                    <input
                      id={`deadline-${classId}-${assignment.lesson_id}`}
                      type="datetime-local"
                      value={deadlineValue}
                      disabled={busy || !assignment.submissions_enabled}
                      onChange={(e) => setDeadlineDrafts((drafts) => ({ ...drafts, [assignment.lesson_id]: e.target.value }))}
                      onBlur={() => commitDeadline(assignment)}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitDeadline(assignment); }}
                      className="input w-auto min-w-0 py-1.5"
                    />
                    {assignment.submission_deadline && (
                      <button
                        onClick={() => updateAssignment(assignment, { submission_deadline: null }, 'Failed to clear the deadline')}
                        disabled={busy}
                        className="link text-xs"
                      >
                        Clear
                      </button>
                    )}
                    {deadlinePassed && (
                      <span className="badge badge-amber normal-case tracking-normal" title={formatDateTime(assignment.submission_deadline)}>
                        Passed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassLessonManager;
